import axios from "axios";
import moment from "moment/moment";
import {useDispatch} from "react-redux";
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {setLoader} from "../../store/slice/dashboard";
import {
    useGetAllSectionsMutation,
    useLazyGetAllProjectsQuery,
    useLazyGetAllReportsQuery
} from "../../store/service/adbService";
import {
    useLazyGetQuarterlyReportSectionsQuery
} from "../../store/service/quartelyApi";
import {
    useLazyGetProcurementPackageQuery,
    useLazyGetProcurementReportSectionsQuery
} from "../../store/service/procurement";
import ActionButtons from "./ActionButtons";
import DetailsSection from "./DetailsSection";
import DateSelector from "./DateSelector";

const API_PATHS = {
    1: "report/submitted",
    2: "quarterly-report/submitted-reports",
    // 3: "procurement-package/submitted-reports",
};

const ProjectReports = ({isAllProject, tab, translations}) => {
    const dispatch = useDispatch();
    const [reportId, setReportId] = useState(null);
    const [details, setDetails] = useState([]);
    const [originDetails, setOriginDetails] = useState({});
    const [selectedDate, setSelectedDate] = useState();
    const [reportDates, setReportDates] = useState([]);
    const [selectedItem, setSelectedItem] = useState({});
    const [projectOptions, setProjectOptions] = useState([]);
    const [selectedDetails, setSelectedDetails] = useState([]);
    const [parsedDetails, setParsedDetails] = useState([]);
    const [projectPage, setProjectPage] = useState(1);
    const [projectTotalPages, setProjectTotalPages] = useState(1);
    const [projectSearch, setProjectSearch] = useState("");
    const searchDebounceRef = useRef(null);

    const [getAllSections, {data: allProjectSections}] = useGetAllSectionsMutation()
    const [getQuarterlyReportSections, {data: allReportSections}] = useLazyGetQuarterlyReportSectionsQuery();
    const [getAllProjects, {data: allProjects, isFetching: projectsFetching}] = useLazyGetAllProjectsQuery();
    const [getAllReports, {data: allReports}] = useLazyGetAllReportsQuery();
    const [getAllPPackages, {data: allPPackages,}] = useLazyGetProcurementPackageQuery();
    const [getAllPPackageSections, {data: allPPackageSections}] = useLazyGetProcurementReportSectionsQuery();


    const lanCode = localStorage.getItem("local_lan");
    moment.locale(lanCode);

    const requestHeaders = {
        "Content-Type": "multipart/form-data",
        "AppLocale": `${lanCode ? lanCode : 'en'}`,
        Authorization: `Bearer ` + localStorage.getItem("user_token")
    };

    const fetchData = useCallback(() => {
        setDetails([]);
        setReportDates([]);
        setProjectPage(1);
        setProjectSearch("");
        setProjectOptions([]);
        if (tab === 1) {
            getAllProjects({
                per_page: 20,
                page: 1,
                search: "",
                all_projects: isAllProject ? 0 : 1
            });
            getAllSections();
        } else if (tab === 2) {
            getAllReports();
            getQuarterlyReportSections();
        }
        // else if (tab === 3) {
        //     getAllPPackages({
        //         isAllPackages: isAllProject ? 0 : 1,
        //         per_page: 100000000
        //     })
        //     getAllPPackageSections();
        // }
    }, [tab, isAllProject]);

    const loadMoreProjects = useCallback(() => {
        if (projectsFetching || projectPage >= projectTotalPages) return;
        const nextPage = projectPage + 1;
        setProjectPage(nextPage);
        getAllProjects({
            per_page: 20,
            page: nextPage,
            search: projectSearch,
            all_projects: isAllProject ? 0 : 1
        });
    }, [projectsFetching, projectPage, projectTotalPages, projectSearch, isAllProject, getAllProjects]);

    const handleProjectSearch = useCallback((inputValue) => {
        setProjectSearch(inputValue);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setProjectPage(1);
            setProjectOptions([]);
            getAllProjects({
                per_page: 20,
                page: 1,
                search: inputValue,
                all_projects: isAllProject ? 0 : 1
            });
        }, 400);
    }, [isAllProject, getAllProjects]);

    const mapToOptions = (data, labelKey, valueKey) =>
        data?.map((item) => ({
            label: item[labelKey],
            value: item[valueKey],
        }));

    useEffect(()=>{
        fetchData();
    },[fetchData])

    useEffect(() => {
        if (tab === 1 && allProjects?.data?.data) {
            setProjectTotalPages(allProjects?.data?.last_page ?? 1);
            const newOptions = mapToOptions(allProjects?.data?.data, "project_name", "id");
            setProjectOptions((prev) =>
                projectPage === 1 ? newOptions : [...prev, ...newOptions]
            );
        }
    }, [allProjects]);

    useEffect(() => {
        setReportId(null);
        setSelectedDate(null);
        setSelectedItem({});
        setSelectedDetails([]);
        if (tab === 2 && allReports?.data) {
            setProjectOptions(mapToOptions(allReports?.data, "report_name", "id"));
        }
        // if (tab ===3 && allPPackages?.data?.data ){
        //     setProjectOptions(mapToOptions(allPPackages?.data?.data, "title", "id"))
        // }
    }, [tab, allReports, allPPackages]);

    useEffect(() => {
        if (tab === 1 && allProjectSections?.data) {
            setOriginDetails(allProjectSections?.data)
        }
        if (tab === 2 && allReportSections?.data) {
            setOriginDetails(allReportSections?.data)
        }
        // if (tab === 3 && allPPackageSections?.data) {
        //     setOriginDetails(allPPackageSections?.data)
        // }

    }, [tab, allReportSections, allProjectSections, allPPackageSections])

    useEffect(() => {
        if (selectedItem?.value !== undefined) {
            dispatch(setLoader(true));
            axios
                .get(
                    `${process.env.REACT_APP_API_URL}/${API_PATHS[tab]}/${selectedItem?.value}`,
                    {headers: requestHeaders}
                )
                .then((res) => {
                    dispatch(setLoader(false));
                    setReportDates(res?.data?.data?.map((date) =>  ({
                                value: date?.id,
                                label: moment(date?.date)?.format("DD/MM/YYYY")
                            })
                        )
                    )
                    setDetails(null);
                    setSelectedDetails(null);
                })
                .finally(() => {
                    dispatch(setLoader(false));
                });
        }
    }, [selectedItem]);

    useEffect(() => {
        let sectionsData = {};
        for (let i = 0; i < details?.length; i++) {
            const foundItem = selectedDetails.find(item => item.value === details[i].value);
            sectionsData[foundItem.value] = foundItem.show
        }

        setParsedDetails(sectionsData)

    }, [details, selectedDetails])

    function array_move(arr, old_index, new_index) {
        if (new_index >= arr.length) {
            let k = new_index - arr.length + 1;
            while (k--) {
                arr.push(undefined);
            }
        }
        arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
        return arr;
    }

    useEffect(() => {
        if (selectedDate) {
            dispatch(setLoader(true));
            axios
                .get(
                    `${process.env.REACT_APP_API_URL}/${
                        tab === 1 ? 'projects/settings-by-report' :
                            tab === 2 ? 'quarterly-report/sections-by-report' :
                                'procurement-package/report/sections'
                    }/${reportId?.value}`,
                    {headers: requestHeaders}
                )
                .then((res) => {
                    dispatch(setLoader(false));
                    let s = [];
                    for (let key in res?.data?.data) {
                        s = [
                            ...s,
                            {
                                label: originDetails?.[key],
                                value: key,
                                show: res?.data?.data[key] ? 1 : 0,
                            },
                        ];
                    }
                    if (tab === 1) {
                        array_move(s, 5, 26)
                    }
                    setDetails(s);
                    setSelectedDetails(s);
                })
                .catch(() => {
                    dispatch(setLoader(false));
                });
        }
    }, [selectedDate]);

    return (
        <div className="reporting__contentContainer">
            <DateSelector
                tab={tab}
                translations={translations}
                projectOptions={projectOptions}
                setReportId={setReportId}
                selectedDate={selectedDate}
                reportDates={reportDates}
                setSelectedDate={setSelectedDate}
                setSelectedItem={setSelectedItem}
                selectedItem={selectedItem}
                onScrollToBottom={loadMoreProjects}
                isLoadingMore={projectsFetching}
                onProjectSearch={handleProjectSearch}
            />
            <DetailsSection
                tab={tab}
                details={details}
                translations={translations}
                selectedDate={selectedDate}
                selectedDetails={selectedDetails}
                setSelectedDetails={setSelectedDetails}
            />
            <ActionButtons
                tab={tab}
                reportId={reportId}
                allReports={allReports}
                selectedDate={selectedDate}
                translations={translations}
                parsedDetails={parsedDetails}
                reportDates={reportDates}
                selectedItem={selectedItem}
                selectedDetails={selectedDetails}
                requestHeaders={requestHeaders}
            />
        </div>
    )
}

export default ProjectReports;
